import React from "react"
import {
  Card,
  Col,
  Descriptions,
  Drawer,
  Row,
  Spin,
  Table,
  Tag,
  notification,
} from "antd"
import { useAppData } from "src/store/app-data"
import api from "src/api"
import styled from "styled-components"

const receiptTypeLabels = {
  registerWithSubscription: "Регистрация (подписка)",
  registerWithPost: "Регистрация (POST)",
  getStatus: "Проверка статуса",
  reject: "Возврат",
  ofdCreatePrepayment: "ОФД создание предоплаты",
  ofdCreateFullPayment: "ОФД создание полной оплаты",
  ofdConfirmPrepayment: "ОФД подтверждение предоплаты",
  ofdConfirmFullPayment: "ОФД подтверждение полной оплаты",
}

const receiptColumns = [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    width: 60,
  },
  {
    title: "Тип",
    dataIndex: "type",
    key: "type",
    render: (val) => receiptTypeLabels[val] ?? val,
  },
  {
    title: "Сумма",
    dataIndex: "sum",
    key: "sum",
    width: 100,
  },
  {
    title: "Статус",
    dataIndex: "status",
    key: "status",
    width: 100,
    render: (val) => (
      <Tag color={val === "SUCCESS" ? "green" : val ? "red" : "default"}>
        {val ?? "—"}
      </Tag>
    ),
  },
  {
    title: "Тип оплаты",
    dataIndex: "paymentType",
    key: "paymentType",
    width: 120,
  },
  {
    title: "Режим оплаты",
    dataIndex: "paymentMode",
    key: "paymentMode",
    width: 140,
  },
  {
    title: "GUID",
    dataIndex: "requestGuid",
    key: "requestGuid",
    width: 200,
    ellipsis: true,
  },
  {
    title: "Дата",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 160,
  },
]

export function ReceiptDetailDrawer({ orderId, onClose }) {
  const layoutConfig = useAppData((state) => state.layoutConfig)
  const [isLoading, setIsLoading] = React.useState(true)
  const [order, setOrder] = React.useState(null)
  const [receipts, setReceipts] = React.useState([])

  React.useEffect(() => {
    if (orderId) {
      loadDetail()
    }
  }, [orderId])

  async function loadDetail() {
    setIsLoading(true)
    try {
      const { data } = await api.get(`receipts/${orderId}`, {
        useV2Api: true,
      })
      setOrder(data.order ?? null)
      setReceipts(
        (data.receipts ?? []).map((r) => ({ ...r, key: r.id }))
      )
    } catch (error) {
      console.error("Ошибка загрузки чеков", error)
      notification.error({ message: "Ошибка загрузки чеков" })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <StyledDrawer
      width={layoutConfig.isMobile ? "95%" : "75%"}
      title={`Чеки заказа #${orderId}`}
      onClose={onClose}
      open
    >
      {isLoading ? (
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      ) : (
        <>
          {order && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
                <Descriptions.Item label="ID заказа">
                  {order.id}
                </Descriptions.Item>
                <Descriptions.Item label="Внутр. номер">
                  {order.innerId ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Статус">
                  {order.orderStatus ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Тип оплаты">
                  {order.paymentType ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Оплачен">
                  <Tag color={order.isPaid ? "green" : "red"}>
                    {order.isPaid ? "Да" : "Нет"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Сумма">
                  {order.totalPriceWithDiscount ?? order.totalPrice}
                </Descriptions.Item>
                <Descriptions.Item label="Дата доставки">
                  {order.deliveryDate ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="External GUID">
                  {order.externalGuid ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Online Payment ID">
                  {order.onlinePaymentId ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Зарег. в ОФД">
                  <Tag color={order.isRegOfd ? "green" : "orange"}>
                    {order.isRegOfd ? "Да" : "Нет"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Сохранён в ОФД">
                  <Tag color={order.isSaveOfd ? "green" : "orange"}>
                    {order.isSaveOfd ? "Да" : "Нет"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Маркировка">
                  <Tag color={order.hasMarkProducts ? "blue" : "default"}>
                    {order.hasMarkProducts ? "Да" : "Нет"}
                  </Tag>
                </Descriptions.Item>
                {order.sumOnOfd != null && (
                  <Descriptions.Item label="Сумма ОФД">
                    {order.sumOnOfd}
                  </Descriptions.Item>
                )}
                {order.refusedSum != null && (
                  <Descriptions.Item label="Возвраты">
                    {order.refusedSum}
                  </Descriptions.Item>
                )}
                {order.deltaOfd != null && (
                  <Descriptions.Item label="Дельта ОФД">
                    {order.deltaOfd}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          <Table
            dataSource={receipts}
            columns={receiptColumns}
            pagination={false}
            size="small"
            scroll={{ x: 900 }}
            expandable={{
              expandedRowRender: (record) => (
                <Row gutter={16}>
                  {record.requestToBank && (
                    <Col span={12}>
                      <Card size="small" title="Запрос в банк">
                        <pre style={{ fontSize: 11, maxHeight: 300, overflow: "auto" }}>
                          {JSON.stringify(record.requestToBank, null, 2)}
                        </pre>
                      </Card>
                    </Col>
                  )}
                  {record.responseFromBank && (
                    <Col span={12}>
                      <Card size="small" title="Ответ банка">
                        <pre style={{ fontSize: 11, maxHeight: 300, overflow: "auto" }}>
                          {JSON.stringify(record.responseFromBank, null, 2)}
                        </pre>
                      </Card>
                    </Col>
                  )}
                </Row>
              ),
              rowExpandable: (record) =>
                record.requestToBank || record.responseFromBank,
            }}
          />
        </>
      )}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)``

const SpinWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 80px 0;
`
